import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { ActionButton, ButtonConfiguration, PacienteRowData, TratamientoType } from '../models/pacientes-view.models';
import { ReportesV2Service } from '@reportes/services/reportes-v2.service';
import { PermissionService, RECURSOS } from '@auth/services/permission.service';
import { OverlayState } from '../components/action-overlay/action-overlay';
import { Pacientes } from './pacientes';

@Injectable({
  providedIn: 'root',
})
export class PacientesViewService {
  private router = inject(Router);
  private reportesV2Service = inject(ReportesV2Service);
  private permissionService = inject(PermissionService);
  private pacientesService = inject(Pacientes);

  // State signals
  public selectedRow = signal<PacienteRowData | null>(null);
  public isRowSelected = computed(() => this.selectedRow() !== null);

  // Download state
  public downloadState = signal<OverlayState>('idle');
  public downloadError = signal<string | null>(null);

  // Delete state
  public deleteState = signal<OverlayState>('idle');
  public deleteError = signal<string | null>(null);

  private onDeleteSuccessCallback: (() => void) | null = null;

  public registerOnDeleteSuccess(cb: () => void): void {
    this.onDeleteSuccessCallback = cb;
  }

  // Map grid tratamiento values to button config keys
  private tratamientoMap: Record<string, TratamientoType> = {
    TUBERCULOSIS_SENSIBLE: 'TUBERCULOSIS_SENSIBLE',
    TUBERCULOSIS_FARMACORRESISTENTE: 'TUBERCULOSIS_FARMACORRESISTENTE',
    LEPRA: 'LEPRA',
    QUIMIOPROFILAXIS: 'QUIMIOPROFILAXIS',
  };

  // Get route treatment type (lowercase with underscores)
  private getRouteTreatmentType(tratamiento: TratamientoType): string {
    const map: Record<TratamientoType, string> = {
      TUBERCULOSIS_SENSIBLE: 'tuberculosis_sensible',
      TUBERCULOSIS_FARMACORRESISTENTE: 'tuberculosis_farmacorresistente',
      LEPRA: 'lepra',
      QUIMIOPROFILAXIS: 'quimioprofilaxis',
    };
    return map[tratamiento];
  }

  // Button configurations for each tratamiento type
  private buttonConfigs: Record<TratamientoType, ButtonConfiguration> = {
    TUBERCULOSIS_SENSIBLE: {
      top_buttons: [
        {
          label: 'Ver',
          route: 'ingreso-paciente/crear_paciente_TBS/:caso_id',
          icon: 'pi pi-eye',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.VER_PACIENTE_TBS,
        },
        {
          label: 'Tarjeta',
          icon: 'pi pi-download',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          command: () => this.descargarTarjeta('TUBERCULOSIS_SENSIBLE'),
          recurso: RECURSOS.DESCARGAR_CASOS_TBS,
        },
        {
          label: 'Georreferenciación',
          route: 'georreferenciacion/:persona_id',
          icon: 'pi pi-map-marker',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.INGRESO_GEORREFERENCIACION,
        },
        {
          label: 'Eliminar',
          icon: 'pi pi-trash',
          severity: 'danger',
          light_class: 'text-red-600',
          dark_class: 'text-red-400',
          recurso: RECURSOS.ELIMINAR_PACIENTE,
          command: () => this.eliminarCaso(),
        },
      ],
      bottom_buttons: [
        {
          label: 'Contactos',
          route: 'contactos-tbs/:persona_id',
          icon: 'pi pi-users',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.INGRESO_MODULO_TBS,
        },
        {
          label: 'Tratamiento',
          route: 'tratamiento/tuberculosis_sensible/:caso_id',
          icon: 'pi pi-list-check',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.INGRESO_TRATAMIENTO_TBS,
        },
        {
          label: 'Egreso',
          route: 'egreso-tbs/:caso_id',
          icon: 'pi pi-sign-out',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.INGRESO_EGRESO_TBS,
        },
      ],
    },
    TUBERCULOSIS_FARMACORRESISTENTE: {
      top_buttons: [
        {
          label: 'Ver',
          route: 'ingreso-paciente/crear_paciente_TBF/:caso_id',
          icon: 'pi pi-eye',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.VER_PACIENTE_TBF,
        },
        {
          label: 'Tarjeta',
          icon: 'pi pi-download',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          command: () => this.descargarTarjeta('TUBERCULOSIS_FARMACORRESISTENTE'),
          recurso: RECURSOS.DESCARGAR_CASOS_TBF,
        },
        {
          label: 'Georreferenciación',
          route: 'georreferenciacion/:persona_id',
          icon: 'pi pi-map-marker',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.INGRESO_GEORREFERENCIACION,
        },
        {
          label: 'Eliminar',
          icon: 'pi pi-trash',
          severity: 'danger',
          light_class: 'text-red-600',
          dark_class: 'text-red-400',
          recurso: RECURSOS.ELIMINAR_PACIENTE,
          command: () => this.eliminarCaso(),
        },
      ],
      bottom_buttons: [
        {
          label: 'Contactos',
          route: 'contactos-tbf/:persona_id',
          icon: 'pi pi-users',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.INGRESO_MODULO_TBF,
        },
        {
          label: 'Tratamiento',
          route: 'tratamiento/tuberculosis_farmacorresistente/:caso_id',
          icon: 'pi pi-list-check',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.INGRESO_TRATAMIENTO_TBF,
        },
        {
          label: 'Egreso',
          route: 'egreso-tbf/:caso_id',
          icon: 'pi pi-sign-out',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.INGRESO_EGRESO_TBF,
        },
      ],
    },
    LEPRA: {
      top_buttons: [
        {
          label: 'Ver',
          route: 'ingreso-paciente/crear_paciente_LPA/:caso_id',
          icon: 'pi pi-eye',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.VER_PACIENTE_LPA,
        },
        {
          label: 'Tarjeta',
          icon: 'pi pi-download',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          command: () => this.descargarTarjeta('LEPRA'),
          recurso: RECURSOS.DESCARGAR_CASOS_LPA,
        },
        {
          label: 'Eliminar',
          icon: 'pi pi-trash',
          severity: 'danger',
          light_class: 'text-red-600',
          dark_class: 'text-red-400',
          recurso: RECURSOS.ELIMINAR_PACIENTE,
          command: () => this.eliminarCaso(),
        },
      ],
      bottom_buttons: [
        {
          label: 'Convivientes',
          route: 'convivientes/:persona_id',
          icon: 'pi pi-home',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.INGRESO_MODULO_LPA,
        },
        {
          label: 'Tratamiento',
          route: 'tratamiento/lepra/:caso_id',
          icon: 'pi pi-list-check',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.INGRESO_TRATAMIENTO_LPA,
        },
        {
          label: 'Egreso',
          route: 'egreso/lepra/:caso_id',
          icon: 'pi pi-sign-out',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.INGRESO_EGRESO_LPA,
        },
      ],
    },
    QUIMIOPROFILAXIS: {
      top_buttons: [
        {
          label: 'Ver',
          route: 'ingreso-paciente/crear_paciente_QMO/:caso_id',
          icon: 'pi pi-eye',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.VER_PACIENTE_QMO,
        },
        {
          label: 'Tarjeta',
          icon: 'pi pi-download',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          command: () => this.descargarTarjeta('QUIMIOPROFILAXIS'),
          recurso: RECURSOS.DESCARGAR_CASOS_QMO,
        },
        {
          label: 'Eliminar',
          icon: 'pi pi-trash',
          severity: 'danger',
          light_class: 'text-red-600',
          dark_class: 'text-red-400',
          recurso: RECURSOS.ELIMINAR_PACIENTE,
          command: () => this.eliminarCaso(),
        },
      ],
      bottom_buttons: [
        {
          label: 'Tratamiento',
          route: 'tratamiento/quimioprofilaxis/:caso_id',
          icon: 'pi pi-list-check',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.INGRESO_MODULO_QMO,
        },
        {
          label: 'Egreso',
          route: 'egreso-qmo/:caso_id',
          icon: 'pi pi-sign-out',
          severity: 'secondary',
          light_class: 'text-emerald-600',
          dark_class: 'text-emerald-400',
          recurso: RECURSOS.INGRESO_EGRESO_QMO,
        },
      ],
    },
  };

  private filterByPermission = (btn: { recurso?: string }) =>
    !btn.recurso || this.permissionService.hasPermission(btn.recurso);

  public currentButtons = computed<ButtonConfiguration | null>(() => {
    const row = this.selectedRow();
    if (!row || !row.tratamiento) {
      return null;
    }
    const tratamiento = row.tratamiento.replace(/ /g, '_');
    const tratamientoType = this.tratamientoMap[tratamiento];
    if (!tratamientoType) {
      return null;
    }

    const config = this.buttonConfigs[tratamientoType];

    return {
      top_buttons: config.top_buttons.filter(this.filterByPermission),
      bottom_buttons: config.bottom_buttons.filter(this.filterByPermission),
    };
  });

  // Set selected row
  public setSelectedRow(row: PacienteRowData | null): void {
    this.selectedRow.set(row);
  }

  // Clear selection
  public clearSelection(): void {
    this.selectedRow.set(null);
  }

  // Replace route parameters with actual values
  public resolveRoute(route: string, row: PacienteRowData): string {
    let resolvedRoute = route;
    const key = route?.split('/').pop()?.replace(/:/g, '');
    const value = row[key as keyof PacienteRowData]!.toString();
    if (key && value) {
      resolvedRoute = resolvedRoute.replace(`:${key}`, value);
    }
    return resolvedRoute;
  }

  // Tarjeta action
  public descargarTarjeta(tratamientoType: TratamientoType): void {
    const row = this.selectedRow();
    if (!row?.caso_id) return;

    this.downloadState.set('loading');
    this.downloadError.set(null);

    this.reportesV2Service
      .descargarReporte({
        tratamiento: tratamientoType,
        tipo_reporte: 'TARJETA',
        caso_id: row.caso_id,
      })
      .subscribe({
        next: () => {
          this.downloadState.set('success');
          setTimeout(() => this.downloadState.set('idle'), 2000);
        },
        error: (err: HttpErrorResponse) => {
          this._handleDownloadError(err);
        },
      });
  }

  public dismissDownload(): void {
    this.downloadState.set('idle');
    this.downloadError.set(null);
  }

  private _handleDownloadError(err: HttpErrorResponse): void {
    if (err.error instanceof Blob) {
      // API returned an error as blob — read it as text
      const reader = new FileReader();
      reader.onload = () => {
        const text = reader.result as string;
        try {
          const json = JSON.parse(text);
          this.downloadError.set(json.detail || text);
        } catch {
          this.downloadError.set(text);
        }
        this.downloadState.set('error');
      };
      reader.readAsText(err.error);
    } else {
      this.downloadError.set(err.message || 'Error desconocido');
      this.downloadState.set('error');
    }
  }

  // Eliminar Caso action
  public eliminarCaso(): void {
    const row = this.selectedRow();
    if (!row?.caso_id) return;

    this.deleteState.set('loading');
    this.deleteError.set(null);

    this.pacientesService.softDeleteCaso(row.caso_id).subscribe({
      next: () => {
        this.deleteState.set('success');
        this.clearSelection();
        this.onDeleteSuccessCallback?.();
        setTimeout(() => this.deleteState.set('idle'), 2000);
      },
      error: (err: HttpErrorResponse) => {
        this.deleteError.set(err?.error?.detail ?? err.message ?? 'Error desconocido');
        this.deleteState.set('error');
      },
    });
  }

  public dismissDelete(): void {
    this.deleteState.set('idle');
    this.deleteError.set(null);
  }
}
